import { motion } from 'motion/react'
import { ArrowRight, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/cn'
import type { IntakeFormData, BudgetRange } from '@/hooks/useIntakeWizard'

interface BudgetStepProps {
  formData: IntakeFormData
  updateField: <K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => void
  onNext: () => void
  onBack: () => void
}

interface BudgetOption {
  value: BudgetRange
  label: string
  hint: string
}

// Values must stay in sync with BudgetRange in useIntakeWizard.ts.
// Labels/hints mirror the modular model in @gjl/voice/pricing (self-serve
// capabilities -> packages -> managed services -> festival lane/concierge).
const BUDGET_OPTIONS: BudgetOption[] = [
  { value: '20-50', label: '$20 – $50/mo', hint: 'Self-serve: website, email' },
  { value: '50-150', label: '$50 – $150/mo', hint: 'Self-serve packages + add-ons, run it yourself' },
  { value: '150-300', label: '$150 – $300/mo', hint: 'One managed service, run for you' },
  { value: '300-600', label: '$300 – $600/mo', hint: 'Managed bundle or Festival Platform' },
  { value: '600-plus', label: '$600+/mo', hint: 'Concierge: everything done for you' },
  { value: 'unsure', label: 'Not sure yet', hint: "Let's discuss" },
]

export function BudgetStep({ formData, updateField, onNext, onBack }: BudgetStepProps) {
  const handleSelect = (value: BudgetRange) => {
    updateField('budgetRange', value)
    // Auto-advance after selection
    setTimeout(() => onNext(), 150)
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground text-3xl font-bold md:text-4xl">What's your monthly budget?</h2>
        <p className="text-muted-foreground text-lg">
          This is your total monthly spend — platform + setup + ongoing services. Setup fees can be spread over 12
          months so your monthly stays predictable.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid gap-3"
      >
        {BUDGET_OPTIONS.map((option, index) => (
          <motion.button
            key={option.value}
            type="button"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            onClick={() => handleSelect(option.value)}
            className={cn(
              'w-full rounded-lg border-2 px-6 py-4 text-left transition-all',
              'hover:border-primary/50 hover:bg-primary/5',
              formData.budgetRange === option.value ? 'border-primary bg-primary/5' : 'border-border'
            )}
          >
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-lg font-medium">{option.label}</span>
              <span className="text-muted-foreground text-sm">{option.hint}</span>
            </div>
          </motion.button>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button
          type="button"
          size="lg"
          onClick={onNext}
          disabled={!formData.budgetRange}
          className="flex-1 md:min-w-[200px] md:flex-none"
        >
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="text-muted-foreground text-sm"
      >
        Step 4 of 5
      </motion.p>
    </div>
  )
}
